import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useAuth from '../auth/useAuth';
import useIsMobile from '../auth/useIsMobile';
import Loading from '../components/Loading';
import Modal from '../components/Modal';
import PlantsMap from '../components/PlantsMap';
import { TextField, TextArea } from '../components/FormField';
import { listTeam } from '../services/team';
import { listPlants } from '../services/plants';
import { listSuppliers } from '../services/suppliers';
import { listParts } from '../services/parts';
import {
  createUpdate,
  deleteUpdate,
  listUpdates,
  updateUpdate,
} from '../services/companyUpdates';
import { listRecentContactEvents } from '../services/salesTracker';
import { displayFullName } from '../utils/teamDisplay';
import { formatDate } from '../utils/formatters';

/* global __BUILD_TIME__ */
// Build timestamp injected by Vite's `define` (see vite.config.js). It's
// fixed at the moment `npm run build` ran — i.e. when the portal was last
// rebuilt and redeployed — and formatted as "M/D/YYYY at H:MM AM/PM" for
// the Dashboard header. Guarded so a missing define never breaks render.
const PORTAL_LAST_UPDATED = (() => {
  try {
    const iso = typeof __BUILD_TIME__ !== 'undefined' ? __BUILD_TIME__ : null;
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    const date = d.toLocaleDateString('en-US');
    const time = d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    });
    return `${date} at ${time}`;
  } catch {
    return null;
  }
})();

/**
 * Dashboard: a home page that rolls up light metrics from each list and
 * surfaces quick-actions appropriate to the signed-in role.
 *
 * The layout is role-aware:
 *   - Director        — company-wide metrics + finance, full quick actions
 *   - RegionalManager — "your team / your plants" scoping + approvals actions
 *   - LiaisonManager  — "your liaisons / your plants" scoping
 *   - Liaison         — personal context (your plant, your vehicles) and
 *                       day-to-day submit actions
 *
 * We pull each list in parallel and tolerate individual failures — a bad list
 * shouldn't blank the whole page. Errors just zero the relevant metric.
 */
export default function Dashboard() {
  const {
    name,
    email,
    isDirector,
    isExecutive,
    isRegionalManager,
    isLiaisonManager,
    isLiaison,
  } = useAuth();
  // News & Announcements: Director, Executive, Regional Manager can post.
  const canPostUpdate = isDirector || isExecutive || isRegionalManager;
  // Who may edit/delete a given post: Directors can manage ANY announcement
  // (moderation); Executives and Regional Managers can manage only the ones
  // they posted themselves. A post is "yours" when its PostedBy matches your
  // name (or email) — the same value used when the post was created.
  const myPostedBy = (name || email || '').trim().toLowerCase();
  const canManagePost = (u) => {
    if (isDirector) return true;
    if (!canPostUpdate) return false;
    const author = (u.PostedBy || '').trim().toLowerCase();
    return !!myPostedBy && author === myPostedBy;
  };
  // Quick Actions are hidden on mobile (most fire off edit/request forms).
  const isMobile = useIsMobile();

  const [state, setState] = useState({
    loading: true,
    team: [],
    plants: [],
    suppliers: [],
    parts: [],
    updates: [],
    contactEvents: [],
  });

  // Company-update modal state. `draft === null` means the modal is closed;
  // a draft with no `id` is a new post, one with an id is an edit.
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const safe = (p) => p.catch(() => []);
      const [team, plants, suppliers, parts, updates, contactEvents] =
        await Promise.all([
          safe(listTeam()),
          safe(listPlants()),
          safe(listSuppliers()),
          safe(listParts()),
          safe(listUpdates()),
          safe(listRecentContactEvents({ limit: 15 })),
        ]);
      if (cancelled) return;
      setState({
        loading: false,
        team,
        plants,
        suppliers,
        parts,
        updates,
        contactEvents,
      });
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Derive the numbers we actually render. Keeping this in useMemo means role
  // changes (or a hot-reload) recompute without a refetch, and the loading
  // effect stays small.
  const metrics = useMemo(
    () => computeMetrics(state, { email }),
    [state, email],
  );

  // ---- Company update actions -------------------------------------------
  const refreshUpdates = async () => {
    const updates = await listUpdates().catch(() => []);
    setState((s) => ({ ...s, updates }));
  };

  const startNewUpdate = () => {
    setSaveError(null);
    setDraft({
      Title: '',
      Body: '',
      PostedBy: name || email || '',
      PostedAt: new Date().toISOString(),
    });
  };

  const startEditUpdate = (u) => {
    setSaveError(null);
    setDraft({
      id: u.id,
      Title: u.Title || '',
      Body: u.Body || '',
      PostedBy: u.PostedBy || '',
      PostedAt: u.PostedAt || new Date().toISOString(),
    });
  };

  const saveUpdate = async () => {
    if (!draft || !draft.Title.trim() || !draft.Body.trim()) return;
    setSaving(true);
    setSaveError(null);
    try {
      const payload = {
        Title: draft.Title.trim(),
        Body: draft.Body.trim(),
        PostedBy: draft.PostedBy,
        PostedAt: draft.PostedAt,
      };
      if (draft.id) {
        await updateUpdate(draft.id, payload);
      } else {
        await createUpdate(payload);
      }
      setDraft(null);
      await refreshUpdates();
    } catch (err) {
      // Surface the error so we don't fail silently. Most common causes:
      // (1) CompanyUpdates list not provisioned, (2) user missing write
      // permission on the SharePoint site, (3) required field missing.
      console.error('[Dashboard] saveUpdate failed', err);
      setSaveError(
        err?.body?.error?.message ||
        err?.message ||
        'Something went wrong saving the update. Check the browser console for details.',
      );
    } finally {
      setSaving(false);
    }
  };

  const removeUpdate = async (u) => {
    if (!window.confirm(`Delete "${u.Title}"? This can't be undone.`)) return;
    await deleteUpdate(u.id);
    await refreshUpdates();
  };

  // Highest role wins when someone has several (e.g. a Director also stubbed
  // in as a RegionalManager). Dashboard variant follows that order.
  // Computed before the loading early-return so the hooks below can use it.
  const variant = isDirector
    ? 'director'
    : isRegionalManager
      ? 'regionalManager'
      : isLiaisonManager
        ? 'liaisonManager'
        : isLiaison
          ? 'liaison'
          : 'generic';

  // Recent Activity sources. Liaison Managers only see activity for their
  // own plants, the suppliers at those plants, and their own liaisons —
  // nothing else (per Jillian, July 2026). Everyone else sees the full
  // company-wide feed.
  const activity = useMemo(() => {
    const all = {
      team: state.team,
      suppliers: state.suppliers,
      plants: state.plants,
      contactEvents: state.contactEvents,
    };
    if (variant !== 'liaisonManager') return all;
    const m = metrics;
    const myPlantCodes = new Set(
      (m.myPlants || [])
        .map((p) => String(p.PlantCode || '').toUpperCase())
        .filter(Boolean),
    );
    const myLiaisonIds = new Set(
      (m.myLiaisons || []).map((x) => String(x.id)),
    );
    const supplierInScope = (s) =>
      (s.plantAssignments || []).some((pa) =>
        myPlantCodes.has(String(pa.plantCode || '').toUpperCase()),
      );
    const supplierCodesInScope = new Set(
      (state.suppliers || [])
        .filter(supplierInScope)
        .map((s) => String(s.SupplierCode || '').toUpperCase())
        .filter(Boolean),
    );
    return {
      team: (state.team || []).filter((x) => myLiaisonIds.has(String(x.id))),
      suppliers: (state.suppliers || []).filter(supplierInScope),
      plants: (state.plants || []).filter((p) =>
        myPlantCodes.has(String(p.PlantCode || '').toUpperCase()),
      ),
      contactEvents: (state.contactEvents || []).filter((ev) => {
        const pc = String(ev.PlantCode || '').toUpperCase();
        const sc = String(ev.SupplierCode || '').toUpperCase();
        // A plant-tagged event must be at one of MY plants. A supplier
        // that also serves my plant doesn't make its activity at OTHER
        // plants mine — only show activity for plants I'm assigned to
        // (per Jillian, Aug 2026).
        if (pc) return myPlantCodes.has(pc);
        // Supplier-level events (no plant code) show when the supplier
        // serves one of my plants.
        if (sc) return supplierCodesInScope.has(sc);
        return false;
      }),
    };
  }, [state, variant, metrics]);

  if (state.loading) return <Loading label="Loading dashboard…" />;

  return (
    <>
      <div className="page-header">
        <h1>Welcome{name ? `, ${name.split(' ')[0]}` : ''}</h1>
        {PORTAL_LAST_UPDATED && (
          <span
            className="muted"
            style={{ fontSize: 12, alignSelf: 'center', whiteSpace: 'nowrap' }}
            title="When the portal was last rebuilt and deployed"
          >
            Portal Last Updated: {PORTAL_LAST_UPDATED}
          </span>
        )}
      </div>

      <div className="dashboard-grid">
        {/* Promoted to a tinted orange card so it feels like the home-page
            headline, not just another sidebar block. Pale orange fill +
            brand-colored outline at the standard card border weight. */}
        <div
          className="card"
          style={{
            background: '#FFF5EB',
            borderColor: 'var(--brand-primary, #E87722)',
          }}
        >
          <div className="card-title" style={{ display: 'flex', alignItems: 'center' }}>
            <span>Proxemics News & Announcements</span>
            {canPostUpdate && (
              <button
                className="btn btn-primary btn-sm"
                style={{ marginLeft: 'auto' }}
                onClick={startNewUpdate}
              >
                + New Update
              </button>
            )}
          </div>
          {state.updates.length === 0 && (
            <p className="muted">No recent updates.</p>
          )}
          {state.updates.map((u) => (
            <div key={u.id} className="feed-item">
              <div className="when">
                {formatDate(u.PostedAt)}
                {u.PostedBy && <span className="muted"> · {u.PostedBy}</span>}
              </div>
              <div
                className="who"
                style={{
                  color: 'var(--brand-primary)',
                  fontSize: 18,
                  fontWeight: 700,
                  lineHeight: 1.25,
                  marginBottom: 2,
                }}
              >
                {u.Title}
              </div>
              <div style={{ whiteSpace: 'pre-wrap' }}>{u.Body}</div>
              {canManagePost(u) && (
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => startEditUpdate(u)}
                  >
                    Edit
                  </button>
                  <button
                    className="btn btn-outline btn-sm"
                    onClick={() => removeUpdate(u)}
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {!isMobile && (
          <div className="card">
            <div className="card-title">Quick Actions</div>
            <div className="quick-actions">
              <QuickActions variant={variant} />
            </div>
          </div>
        )}
      </div>

      {/* By the Numbers sits right above Plant Locations so the three
          page-level sections (metrics, map, recent activity) read as one
          stacked column of headings + content. */}
      <div style={{ marginTop: 24 }}>
        <MetricsPanel variant={variant} metrics={metrics} />
      </div>

      {/* "Plant Locations" label matches the "By the Numbers" treatment —
          muted SectionHeading sits outside the card so it reads as a page
          section heading — but the map itself stays wrapped in a card so
          it has the same framed look as the Announcements / Quick Actions
          tiles above. */}
      <div style={{ marginTop: 24 }}>
        <SectionHeading>Plant Locations</SectionHeading>
        <div className="card" style={{ marginTop: 0 }}>
          <PlantsMap plants={state.plants} />
        </div>
      </div>

      {/* ---------- Recent Activity ----------
          Three columns: newest team members, newest suppliers, and newest
          contacts added to any Plant or Supplier. Team + Supplier lists
          fall out of the built-in SharePoint Created timestamp; contact
          events come from the SalesTrackerEvents log (Category='Contact').
          Empty columns render a muted "None yet." so the section never
          looks broken on a fresh tenant. Same pattern as Plant Locations
          above: the heading sits outside the card, the grid inside it. */}
      <div style={{ marginTop: 24 }}>
        <SectionHeading>Recent Activity</SectionHeading>
        <div className="card" style={{ marginTop: 0 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
              gap: 24,
            }}
          >
            <ActivityColumn
              title="New Team Members"
              items={recentlyCreated(activity.team, 15).map((m) => ({
                key: m.id,
                primary: displayName(m),
                secondaryDate: m.Created,
                secondaryPerson: creatorName(m) ? `Added by ${creatorName(m)}` : '',
                to: m.id ? `/team/${m.id}` : null,
              }))}
              emptyLabel="No new team members yet."
            />
            <ActivityColumn
              title="New Suppliers"
              items={recentlyCreated(activity.suppliers, 15).map((s) => ({
                key: s.id,
                primary: s.Title || s.SupplierCode || 'Unnamed supplier',
                secondaryDate: s.Created,
                secondaryPerson: creatorName(s) ? `Added by ${creatorName(s)}` : '',
                to: s.SupplierCode
                  ? `/suppliers/${encodeURIComponent(s.SupplierCode)}`
                  : null,
              }))}
              emptyLabel="No new suppliers yet."
            />
            <ActivityColumn
              title="Contact Changes"
              items={(activity.contactEvents || []).map((ev) => ({
                key: ev.id,
                primary: contactPrimaryLine(ev),
                secondaryDate: ev.PostedAt || ev.Created,
                secondaryPerson: ev.PostedBy ? `Edited by ${ev.PostedBy}` : '',
                to: contactLinkTarget(ev),
              }))}
              emptyLabel="No contact additions yet."
            />
            <ActivityColumn
              title="Plant Changes"
              items={recentlyChanged(activity.plants, 15).map((p) => {
                const created = p.Created ? Date.parse(p.Created) : NaN;
                const modified = p.Modified ? Date.parse(p.Modified) : NaN;
                // Treat a plant whose Modified is essentially its Created
                // time as a brand-new "Added"; anything edited later is an
                // "Updated".
                const isNew =
                  Number.isFinite(created) &&
                  Number.isFinite(modified) &&
                  modified - created < 5000;
                const who = isNew
                  ? creatorName(p)
                  : editorName(p) || creatorName(p);
                const verb = isNew ? 'Added' : 'Updated';
                return {
                  key: p.id,
                  primary: p.Title || p.PlantCode || 'Unnamed plant',
                  secondaryDate: p.Modified || p.Created,
                  secondaryPerson: who ? `${verb} by ${who}` : verb,
                  to: p.PlantCode
                    ? `/plants/${encodeURIComponent(p.PlantCode)}`
                    : null,
                };
              })}
              emptyLabel="No plant changes yet."
            />
          </div>
        </div>
      </div>

      <Modal
        open={!!draft}
        onClose={() => !saving && setDraft(null)}
        title={draft?.id ? 'Edit Announcement' : 'New Announcement'}
      >
        {draft && (
          <>
            <div className="form-grid">
              <TextField
                label="Title"
                value={draft.Title}
                onChange={(v) => setDraft({ ...draft, Title: v })}
                placeholder="Short headline"
                full
              />
              <TextArea
                label="Body"
                value={draft.Body}
                onChange={(v) => setDraft({ ...draft, Body: v })}
                rows={5}
                placeholder="What does the team need to know?"
              />
            </div>
            {saveError && (
              <div
                className="error"
                style={{
                  marginTop: 12,
                  padding: '8px 12px',
                  background: '#fde8e8',
                  color: '#b91c1c',
                  borderRadius: 6,
                  fontSize: 13,
                }}
              >
                {saveError}
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
              <button
                className="btn btn-outline"
                onClick={() => setDraft(null)}
                disabled={saving}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={saveUpdate}
                disabled={saving || !draft.Title.trim() || !draft.Body.trim()}
              >
                {saving ? 'Saving…' : draft.id ? 'Save Changes' : 'Post Update'}
              </button>
            </div>
          </>
        )}
      </Modal>
    </>
  );
}

function Metric({ label, value, delta, deltaLabel, to }) {
  const body = (
    <div className="metric">
      <div className="label">{label}</div>
      <div className="value">{value ?? '—'}</div>
      {delta && <div className={`delta ${delta}`}>{deltaLabel}</div>}
    </div>
  );
  // Optional link wrapper — a tile with `to` set navigates to its page
  // (used by the Liaison Manager tiles, per Jillian July 2026). Inherit
  // color so the tile doesn't turn link-blue.
  if (to) {
    return (
      <Link to={to} style={{ textDecoration: 'none', color: 'inherit' }}>
        {body}
      </Link>
    );
  }
  return body;
}

// Section heading used between metric groups. Styled to sit between the page
// H1 and a metrics-grid without drawing too much attention — same muted tone
// as card titles elsewhere in the app.
function SectionHeading({ children }) {
  return (
    <h2
      style={{
        fontSize: 14,
        fontWeight: 600,
        letterSpacing: '0.04em',
        textTransform: 'uppercase',
        color: 'var(--brand-muted, #64748b)',
        margin: '0 0 8px',
      }}
    >
      {children}
    </h2>
  );
}

// ---- helpers --------------------------------------------------------------

function normalizeName(s) {
  return (s || '').toString().trim().toLowerCase().replace(/\s+/g, ' ');
}

function fullName(m) {
  if (!m) return '';
  if (m.Title && String(m.Title).trim()) return String(m.Title).trim();
  return [m.FirstName, m.LastName].filter(Boolean).join(' ').trim();
}

/**
 * Compute every value any role variant might display. Keeps the render
 * branches small — each variant just picks the tiles it cares about.
 *
 * Personal scoping (myTeam / myLiaisons / myPlants / myParts) is resolved by
 * matching the signed-in user's email to a team row, then following the
 * relationships stored on those records. If we can't find the user on the
 * roster (e.g. a Director who isn't also a team member), personal counts
 * fall back to 0 — the company-wide tiles still render fine.
 */
function computeMetrics(state, { email }) {
  const { team, plants, suppliers, parts } = state;

  const activePlants = (plants || []).filter((p) => {
    const vs = p.vehicles || [];
    if (vs.length) return vs.some((v) => v.status && v.status !== 'Closed');
    return p.Status && p.Status !== 'Closed';
  }).length;

  const activeTeamCount = (team || []).filter(
    (m) => m.EmploymentStatus === 'Active',
  ).length;

  // Find the signed-in user on the team roster so we can scope metrics to
  // them. Match on Proxemics email first (authoritative), fall back to
  // PersonalEmail for anyone whose sign-in happens to be personal.
  const lowerEmail = (email || '').toLowerCase();
  const me =
    (team || []).find(
      (m) =>
        (m.Email && m.Email.toLowerCase() === lowerEmail) ||
        (m.PersonalEmail && m.PersonalEmail.toLowerCase() === lowerEmail),
    ) || null;
  // A team member is referenced from other rows (RegionalManager /
  // liaisonManagers) by NAME, but different rows may have stored a
  // different form of that name — the legal "First Last" (the canonical
  // form), the Title, or a nickname-preferred display name. Build every
  // form `me` could be referenced by and match if a stored name equals
  // ANY of them. Previously we only matched the Title-preferred name, so
  // an LM whose liaisons stored his legal name (e.g. "Arthur Jenuwine"
  // vs. a Title/nickname of "Art") counted 0 liaisons (per Jillian, Aug
  // 2026).
  const myNameForms = new Set(
    [
      fullName(me), // Title-preferred (legacy match)
      displayFullName(me), // nickname-preferred display name
      [me?.FirstName, me?.LastName].filter(Boolean).join(' '), // legal name
      me?.Title || '',
    ]
      .map(normalizeName)
      .filter(Boolean),
  );
  const matchesMe = (n) => myNameForms.size > 0 && myNameForms.has(normalizeName(n));

  const myPlantCodes = new Set((me?.plantCodes || []).filter(Boolean));
  const myPlants = (plants || []).filter((p) => myPlantCodes.has(p.PlantCode));

  // RM view: anyone whose RegionalManager field matches me, Active only.
  const myTeamAsRm = (team || []).filter(
    (m) => m.EmploymentStatus === 'Active' && matchesMe(m.RegionalManager),
  );
  // LM view: anyone whose liaisonManagers array includes me, Active only.
  const myLiaisons = (team || []).filter((m) => {
    if (m.EmploymentStatus !== 'Active') return false;
    const lms = Array.isArray(m.liaisonManagers) ? m.liaisonManagers : [];
    return lms.some(matchesMe);
  });

  // Parts shipped to any of my plants — useful for LM/Liaison dashboards.
  const myParts = (parts || []).filter((pt) =>
    (pt.plantCodes || []).some((code) => myPlantCodes.has(code)),
  );

  // Vehicles across my plants, deduped by name+program. Gives a
  // Liaison a personal "what you work on" chip list. `program`
  // replaces the legacy `modelYear` key — fall back to the old key
  // so dedup still works for plants saved before the rename.
  const myVehicleSet = new Set();
  const myVehicles = [];
  for (const p of myPlants) {
    for (const v of p.vehicles || []) {
      const programLabel = v.program || v.modelYear || '';
      const key = `${v.name || ''}|${programLabel}`;
      if (myVehicleSet.has(key)) continue;
      myVehicleSet.add(key);
      myVehicles.push(v);
    }
  }

  return {
    // company-wide
    activePlants,
    teamCount: activeTeamCount,
    plantCount: (plants || []).length,
    supplierCount: (suppliers || []).length,
    partCount: (parts || []).length,
    // personal
    me,
    myPlants,
    myTeamAsRm,
    myLiaisons,
    myParts,
    myVehicles,
  };
}

function MetricsPanel({ variant, metrics: m }) {
  // Director sees the full company-wide strip plus finance. Other roles get
  // a mix of company-wide context and one or two "your …" tiles so the page
  // feels personal without hiding the bigger picture.
  if (variant === 'director') {
    return (
      <>
        <SectionHeading>By the Numbers</SectionHeading>
        <div className="metrics-grid">
          <Metric label="Plants" value={m.plantCount} />
          <Metric label="Suppliers" value={m.supplierCount} />
          <Metric label="Parts" value={m.partCount} />
          <Metric label="Team Members" value={m.teamCount} />
        </div>
      </>
    );
  }

  if (variant === 'regionalManager') {
    return (
      <>
        <SectionHeading>By the Numbers</SectionHeading>
        <div className="metrics-grid">
          <Metric label="Your Team" value={m.myTeamAsRm.length} />
          <Metric label="Your Plants" value={m.myPlants.length} />
          <Metric label="Team Members" value={m.teamCount} />
          <Metric label="Total Plants" value={m.plantCount} />
          <Metric label="Suppliers" value={m.supplierCount} />
        </div>
      </>
    );
  }

  if (variant === 'liaisonManager') {
    // Each tile links to its page (per Jillian, July 2026): liaisons to
    // the filtered "Your Team" roster, plants and parts to their
    // (already LM-scoped) index pages.
    return (
      <>
        <SectionHeading>By the Numbers</SectionHeading>
        <div className="metrics-grid">
          <Metric
            label="Your Liaisons"
            value={m.myLiaisons.length}
            to="/team?reports=mine"
          />
          <Metric label="Your Plants" value={m.myPlants.length} to="/plants" />
          <Metric
            label="Parts at Your Plants"
            value={m.myParts.length}
            to="/parts"
          />
        </div>
      </>
    );
  }

  if (variant === 'liaison') {
    // Liaisons get a deliberately short, personal read — the plant(s) they're
    // assigned to and the vehicles they'll see on the floor. No company-wide
    // counts since those don't drive their day.
    return (
      <div className="card" style={{ marginBottom: 16 }}>
        <div className="card-title">Your Assignment</div>
        {m.myPlants.length === 0 ? (
          <p className="muted">
            You're not linked to a plant yet. Ask your Liaison Manager to add
            you on the Proxemics Team page.
          </p>
        ) : (
          <>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {m.myPlants.map((p) => (
                <Link
                  key={p.id || p.PlantCode}
                  to={`/plants/${encodeURIComponent(p.PlantCode)}`}
                  className="badge plant"
                  style={{ textDecoration: 'none' }}
                >
                  {p.Title || p.PlantCode}
                </Link>
              ))}
            </div>
            {m.myVehicles.length > 0 && (
              <>
                <div style={{ fontSize: 12, color: 'var(--brand-muted)', marginBottom: 4 }}>
                  Vehicles
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                  {m.myVehicles.map((v, i) => {
                    const composedName =
                      v.name || [v.make, v.model].filter(Boolean).join(' ');
                    const label = [composedName, v.program || v.modelYear]
                      .filter(Boolean)
                      .join(' ');
                    return (
                      <span key={i} className="badge vehicle">
                        {label || '—'}
                      </span>
                    );
                  })}
                </div>
              </>
            )}
          </>
        )}
      </div>
    );
  }

  // Generic fallback — signed in but no portal role recognized.
  return (
    <div className="metrics-grid">
      <Metric label="Plants" value={m.plantCount} />
      <Metric label="Team Members" value={m.teamCount} />
    </div>
  );
}

function QuickActions({ variant }) {
  if (variant === 'director') {
    return (
      <>
        {/* Add Team Member + Add Plant are the everyday director actions
            now that billing approvals / invoicing have moved out of the
            dashboard shortcut row — bumped to `btn-primary` so they read
            as the highlighted calls to action. */}
        <Link className="btn btn-primary" to="/team?new=1">
          Add Team Member
        </Link>
        <Link className="btn btn-primary" to="/plants?new=1">
          Add Plant
        </Link>
        <Link className="btn btn-outline" to="/suppliers?new=1">
          Add Supplier
        </Link>
        <Link className="btn btn-outline" to="/parts?new=1">
          Add Part
        </Link>
      </>
    );
  }
  if (variant === 'regionalManager') {
    return (
      <>
        <Link className="btn btn-primary" to="/timesheets/approvals">
          Review Billing Trackers
        </Link>
        <Link className="btn" to="/timesheets/submit">
          Submit Weekly Billing Tracker
        </Link>
        <Link className="btn btn-outline" to="/team?reports=mine">
          View Your Team
        </Link>
        <Link className="btn btn-outline" to="/parts?new=1">
          Add Part
        </Link>
      </>
    );
  }
  if (variant === 'liaisonManager') {
    // Per Jillian (July 2026): no Submit Daily Report for LMs; the
    // billing tracker action leads, followed by their team, parts, and
    // plants shortcuts.
    return (
      <>
        <Link className="btn btn-primary" to="/timesheets/submit">
          Submit Weekly Billing Tracker
        </Link>
        <Link className="btn btn-outline" to="/team?reports=mine">
          View Your Team
        </Link>
        <Link className="btn btn-outline" to="/parts?new=1">
          Add a New Part
        </Link>
        <Link className="btn btn-outline" to="/plants">
          View Your Plants
        </Link>
      </>
    );
  }
  if (variant === 'liaison') {
    return (
      <>
        <Link className="btn btn-primary" to="/reports?new=1">
          Submit Daily Report
        </Link>
        <Link className="btn" to="/timesheets/submit">
          Submit Weekly Billing Tracker
        </Link>
      </>
    );
  }
  // Generic fallback.
  return (
    <>
      <Link className="btn" to="/reports?new=1">
        Submit Daily Report
      </Link>
      <Link className="btn" to="/timesheets/submit">
        Submit Weekly Billing Tracker
      </Link>
    </>
  );
}

// ---------- Recent Activity helpers --------------------------------------

/**
 * A single column inside the Recent Activity section. Renders a small
 * heading, then a stack of rows — each with a primary line (bold) and a
 * dimmer second line showing when it happened. Rows with a `to` prop
 * link to the relevant detail page; the rest render as plain text.
 */
function ActivityColumn({ title, items, emptyLabel }) {
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          color: 'var(--brand-muted)',
          marginBottom: 10,
        }}
      >
        {title}
      </div>
      {items.length === 0 ? (
        <p className="muted" style={{ marginTop: 0 }}>
          {emptyLabel}
        </p>
      ) : (
        <ul
          style={{
            listStyle: 'none',
            padding: 0,
            margin: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
          }}
        >
          {items.map((item) => (
            <li key={item.key}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>
                {item.to ? (
                  <Link
                    to={item.to}
                    style={{
                      color: 'var(--brand-primary, #E87722)',
                      textDecoration: 'none',
                    }}
                  >
                    {item.primary}
                  </Link>
                ) : (
                  item.primary
                )}
              </div>
              <div
                style={{
                  fontSize: 12,
                  color: 'var(--brand-muted)',
                  marginTop: 2,
                }}
              >
                {formatActivityTimestamp(item.secondaryDate)}
                {item.secondaryPerson ? ` · ${item.secondaryPerson}` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// Display name of whoever created a SharePoint record, surfaced from the
// item's createdBy envelope (added in services/sharepoint listItems). Used
// for the "Added by ___" line in Recent Activity. Returns '' when the
// creator isn't available so callers can omit the label cleanly.
function creatorName(row) {
  return row?.createdBy?.user?.displayName || row?.createdBy?.user?.email || '';
}

// Display name of whoever last EDITED a SharePoint record (from the
// lastModifiedBy envelope). Used for the "Updated by ___" line on the
// Plant Changes activity column. Returns '' when unavailable.
function editorName(row) {
  return (
    row?.lastModifiedBy?.user?.displayName ||
    row?.lastModifiedBy?.user?.email ||
    ''
  );
}

// Sort a list by SharePoint's Modified column (falling back to Created) and
// return the most-recently-touched N. Covers both new records and edits —
// any change bumps Modified — so it reads as a "recent changes" feed.
function recentlyChanged(rows, n) {
  const withTime = (rows || []).map((r) => {
    const t = Date.parse(r.Modified || r.Created || '');
    return { row: r, time: Number.isFinite(t) ? t : 0 };
  });
  withTime.sort((a, b) => b.time - a.time);
  return withTime.slice(0, n).map((w) => w.row);
}

// Sort a list (team, suppliers, etc.) by SharePoint's Created column and
// return the newest N. Missing / unparseable Created values land at the
// bottom so they never push real data off the list.
function recentlyCreated(rows, n) {
  const withTime = (rows || []).map((r) => {
    const t = r.Created ? Date.parse(r.Created) : NaN;
    return { row: r, time: Number.isFinite(t) ? t : 0 };
  });
  withTime.sort((a, b) => b.time - a.time);
  return withTime.slice(0, n).map((w) => w.row);
}

// Display name for a team member in the Recent Activity feed. Prefers
// the synced Title ("First Last") because that's what the Team index
// and org chart use; falls back to FirstName+LastName assembly.
function displayName(m) {
  if (!m) return 'Unnamed';
  if (m.Title && String(m.Title).trim()) return m.Title;
  const composed = [m.FirstName, m.LastName].filter(Boolean).join(' ').trim();
  return composed || 'Unnamed';
}

// "Added Jamie Chen to Sterling Heights Assembly" — assembles the one-line
// description for a Contact event row using the denormalized columns set
// by logContactAddedEvent (PartCode holds the contact name, PartTitle
// holds the parent plant/supplier name).
function contactPrimaryLine(ev) {
  const contact = ev.PartCode || 'Unnamed contact';
  const parent = ev.PartTitle || ev.PlantCode || ev.SupplierCode || '';
  return parent ? `${contact} → ${parent}` : contact;
}

// Where clicking a Contact row should take the user. Prefers the parent's
// detail page (/suppliers/{code} or /plants/{code}) when the event has a
// SupplierCode or PlantCode; otherwise returns null so the row renders as
// plain text.
function contactLinkTarget(ev) {
  if (ev.SupplierCode)
    return `/suppliers/${encodeURIComponent(ev.SupplierCode)}`;
  if (ev.PlantCode) return `/plants/${encodeURIComponent(ev.PlantCode)}`;
  return null;
}

// Compact relative-ish timestamp for the activity feed. Today / Yesterday
// / "Apr 22" for this year / "Apr 22, 2025" otherwise. Keeps the feed
// scannable without a wall of full dates.
function formatActivityTimestamp(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  const now = new Date();
  const startOf = (date) =>
    new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const dayDiff = Math.round((startOf(now) - startOf(d)) / 86400000);
  if (dayDiff === 0) return 'Today';
  if (dayDiff === 1) return 'Yesterday';
  const sameYear = d.getFullYear() === now.getFullYear();
  return d.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}
